import * as mobx from 'mobx';
import {
    ConnectionType,
    IChildConnectionDTO,
    IConnectionDetailsDTO,
    IConnectionGroupDTO,
    IConnectionOrganizationDTO,
    IMeetingConnectionsDTO,
    IParentConnectionDTO
} from '../../../api/connections.dao';
import { IOperatorsDTO, OperatorRole } from '../../../api/operators.dao';
import { FormVmValueValidationStatus } from '../../../infrastructure/form-viewmodel-controller';
import { AbstractFormViewModelController } from '../../../infrastructure/implementations/form-viewmodel-controller';
import { ConnectionEntity } from '../abstract-connection.entity';
import { ConnectionAggregateRoot } from '../abstract-connection.entity.aggregate.root';
import formConfig, { MeetingConnectionFields as formFields } from '../config/meeting-connection-config';
import { meetingRoleConnectionFieldByRole, organizationRoleList } from '../const';

import { OrganizationRole } from '../types';
import { connectionIdValueTypeGuard, existedOrganizationRoleGuard } from '../types-guard';
import { OperatorItemsMapper } from '../../operators/operator-items.mapper';
import { OrganizationMeetingConnection } from './organization-meeting-connection.aggregate';
import { mergeConnectionOrganizations } from '../../../infrastructure/utils/attendee.utils';
import { IAttendeeOrganizationDTO } from '../../../api/attendees.dao';
import { isBefore } from 'date-fns';
import { ConnectionVersion } from '../../../constants/common.constants';
import {
    IDropdownConnectionGroupDetails
} from '../../../components/_events/_manage-event/_connections/_connection_participants/attendee-unique-connection-indicator-wrapper/attendee-unique-connection-group-indicator/attendee-unique-connection-group-indicator';
import { transpileClassesToInlineStyles } from '../../../components/global/rich-text-editor/utils/transpiler';

export type MeetingConnectionsMapType = Record<IConnectionDetailsDTO['meetingConnectionId'], IConnectionDetailsDTO>;
export type MeetingConnectionGroupsMapType = Record<IConnectionGroupDTO['meetingConnectionId'], IConnectionGroupDTO>;
export type MeetingParentConnectionsMapType = Record<IParentConnectionDTO['meetingConnectionId'], IParentConnectionDTO>;
type RoleOrganizationsMap = Record<IConnectionOrganizationDTO['organizationId'], OrganizationMeetingConnection>;
type InstanceRefType = ThisType<MeetingConnectionsAggregateRoot>;
type OperatorsMap = Record<IOperatorsDTO['email'], IOperatorsDTO>;

type FormFieldProps = {
    setValue(value: string | boolean): void;
    getValue(): string | boolean;
    validationState: FormVmValueValidationStatus;
};

const key = 'defaultConnectionId';

export class MeetingConnectionsAggregateRoot extends ConnectionAggregateRoot<IMeetingConnectionsDTO> {
    private static createFormVMControllerInstance(initial: Partial<IMeetingConnectionsDTO>): AbstractFormViewModelController {
        return new class extends AbstractFormViewModelController {
            constructor() {
                super(initial, 'MeetingConnectionsForm', formConfig);
            }
        }();
    }

    public static mapItemsByKey<T>(items: T[], prop: string, ref: InstanceRefType, mapper?: (item: T) => ConnectionEntity<T>): void {
        items.forEach(function(item) {
            this[item[prop]] = mapper ? mapper(item) : item;
        }, ref);
    }

    public static create(meetingConnectionsDetails: IMeetingConnectionsDTO): MeetingConnectionsAggregateRoot {
        return new MeetingConnectionsAggregateRoot(meetingConnectionsDetails);
    }

    public getId(): number {
        return this.getProps().meetingId;
    }

    public toDTO(): IMeetingConnectionsDTO {
        const _dto = this.getProps();

        this.formFieldsToDTO(_dto);

        _dto.connections = this.getMeetingConnectionList();
        _dto.hosts = this.getOrganizationListByRole(OrganizationRole.Hosts);
        _dto.participants = this.getOrganizationListByRole(OrganizationRole.Participants);
        _dto.presenters = this.getOrganizationListByRole(OrganizationRole.Presenters);
        _dto.supports = this.getOrganizationListByRole(OrganizationRole.Supports);
        _dto.registrants = this.getOrganizationListByRole(OrganizationRole.Registrants);

        _dto.addedOperators = this.getAddedOperators();
        _dto.removedOperators = this.getRemovedOperators();
        _dto.updatedOperators = this.getUpdatedOperators();

        if (this.hasAssignedConnection()) {
            _dto[key] = this.getAssignedConnection();
        } else {
            delete _dto[key];
        }

        this.meetingRoleConnectionsToDTO(_dto);

        if (this.getProps().connectionVersion === ConnectionVersion.V2) {
            _dto.connectionGroups = this.getMeetingConnectionGroupList();
        }

        return _dto as IMeetingConnectionsDTO;
    }

    @mobx.observable
    private [key]: {};
    @mobx.observable
    private meetingRoleConnectionIds: Partial<Record<OrganizationRole, number>> = {};
    @mobx.observable
    private _connections: MeetingConnectionsMapType = {};
    @mobx.observable
    private connectionGroups: MeetingConnectionGroupsMapType = {};
    @mobx.observable
    private parentConnections: MeetingParentConnectionsMapType = {};
    @mobx.observable
    private [OrganizationRole.Hosts]: RoleOrganizationsMap = {};
    @mobx.observable
    private [OrganizationRole.Participants]: RoleOrganizationsMap = {};
    @mobx.observable
    private [OrganizationRole.Presenters]: RoleOrganizationsMap = {};
    @mobx.observable
    private [OrganizationRole.Supports]: RoleOrganizationsMap = {};
    @mobx.observable
    public [OrganizationRole.Registrants]: RoleOrganizationsMap = {};
    @mobx.observable
    private operators: OperatorsMap = {};

    private readonly formFields: AbstractFormViewModelController;

    private readonly operatorsMapper: OperatorItemsMapper;

    private constructor({
        defaultConnectionId,
        presenterRoleConnectionId,
        hostRoleConnectionId,
        supportRoleConnectionId,
        connections,
        connectionGroups = [],
        parentConnections = [],
        meetingConnectionNotes,
        meetingRecordEnabled,
        autoRegistrationEnabled,
        presenters = [],
        hosts = [],
        participants = [],
        registrants = [],
        supports = [],
        operators = [],
        ...rest
    }: IMeetingConnectionsDTO
    ) {
        super(key, rest);
        if (defaultConnectionId) this.assignConnection(defaultConnectionId);
        this.initializeMeetingRoleConnection(OrganizationRole.Presenters, presenterRoleConnectionId);
        this.initializeMeetingRoleConnection(OrganizationRole.Hosts, hostRoleConnectionId);
        this.initializeMeetingRoleConnection(OrganizationRole.Supports, supportRoleConnectionId);
        const { mapItemsByKey, createFormVMControllerInstance } = MeetingConnectionsAggregateRoot;

        mapItemsByKey(connections, 'meetingConnectionId', this._connections);
        mapItemsByKey(operators, 'email', this.operators);
        mapItemsByKey(connectionGroups, 'meetingConnectionId', this.connectionGroups);
        mapItemsByKey(parentConnections, 'meetingConnectionId', this.parentConnections);

        this.setOrganizationsToRole(OrganizationRole.Presenters, presenters);
        this.setOrganizationsToRole(OrganizationRole.Hosts, hosts);
        this.setOrganizationsToRole(OrganizationRole.Participants, participants);
        this.setOrganizationsToRole(OrganizationRole.Supports, supports);
        this.setOrganizationsToRole(OrganizationRole.Registrants, registrants);

        const formFieldsData = {
            meetingConnectionNotes: meetingConnectionNotes,
            meetingRecordEnabled: meetingRecordEnabled,
            autoRegistrationEnabled: autoRegistrationEnabled
        };
        this.formFields = createFormVMControllerInstance(formFieldsData);
        this.operatorsMapper = OperatorItemsMapper.create([...operators]);
    }

    private getAddedOperators(): IOperatorsDTO[] {
        return this.operatorsMapper.getAddedOperatorsToList(this.getOperatorList());
    }
    private getRemovedOperators(): IOperatorsDTO[] {
        return this.operatorsMapper.getRemovedOperatorsFromList(this.getOperatorList());
    }
    private getUpdatedOperators(): IOperatorsDTO[] {
        return this.operatorsMapper.getUpdatedOperatorsInList(this.getOperatorList());
    }


    private formFieldsToDTO(dtoRef: object): void {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        formConfig.forEach(function({ name }) {
            // Since CKEditor works with classes, but data is stored with inline styles,
            // all CKEditor values need to be converted to the appropriate format
            dtoRef[name] = self.transpileFormFieldValueIfFromCKEditor(name, this[name].getValue());
        }, this);
    }

    public get [formFields.MEETING_CONNECTION_NOTES](): FormFieldProps {
        const meetingConnectionNotesKey = formFields.MEETING_CONNECTION_NOTES;
        return {
            setValue: (value: string): void => this.formFields.setValue(meetingConnectionNotesKey, value),
            getValue: (): string => this.formFields.getValue(meetingConnectionNotesKey) as string,
            validationState: this.formFields.getValueValidationState(meetingConnectionNotesKey)
        };
    }

    public get [formFields.MEETING_RECORD_ENABLED](): FormFieldProps {
        const meetingRecordEnabledKey = formFields.MEETING_RECORD_ENABLED;
        return {
            getValue: (): boolean => this.formFields.getValue(meetingRecordEnabledKey) as boolean,
            setValue: (value: boolean): void => this.formFields.setValue(meetingRecordEnabledKey, value),
            validationState: this.formFields.getValueValidationState(meetingRecordEnabledKey)
        };
    }

    public get [formFields.AUTO_REGISTRATION_ENABLED](): FormFieldProps {
        const autoRegKey = formFields.AUTO_REGISTRATION_ENABLED;

        return {
            getValue: (): boolean => this.formFields.getValue(autoRegKey) as boolean,
            setValue: (value: boolean): void => this.formFields.setValue(autoRegKey, value),
            validationState: this.formFields.getValueValidationState(autoRegKey)
        };
    }

    public get [formFields.AUTO_SUBSCRIPTION_ENABLED](): FormFieldProps {
        const autoSubKey = formFields.AUTO_SUBSCRIPTION_ENABLED;
        return {
            getValue: (): boolean => this.formFields.getValue(autoSubKey) as boolean,
            setValue: (value: boolean): void => this.formFields.setValue(autoSubKey, value),
            validationState: this.formFields.getValueValidationState(autoSubKey)
        };
    }

    public isVmValid(): boolean {
        return this.formFields.isVmValid();
    }

    @mobx.action
    public setMeetingConnection(
        connection: IConnectionDetailsDTO,
        isV2Panelist?: boolean,
        meetingConnectionId?: number,
        childPanelistInfo?: IChildConnectionDTO
    ): void {
        if (isV2Panelist) {
            const parentConnection = this._connections[meetingConnectionId];
            if (parentConnection) {
                if (parentConnection.childConnections) {
                    const existingChildIndex = parentConnection.childConnections.findIndex(
                        child => child.joinMethodId === childPanelistInfo.joinMethodId
                    );
                    if (existingChildIndex !== -1) {
                        parentConnection.childConnections[existingChildIndex] = childPanelistInfo;
                    } else {
                        parentConnection.childConnections.push(childPanelistInfo);
                    }
                } else {
                    parentConnection.childConnections = [childPanelistInfo];
                }
            }
        } else {
            this._connections[connection.meetingConnectionId] = connection;
        }
    }
    @mobx.action
    public removeMeetingConnectionById(meetingConnectionId: number): void {
        this.removeMeetingConnectionFromGroups(this._connections[meetingConnectionId]);
        delete this._connections[meetingConnectionId];
        this.invalidateAssignedConnectionById(meetingConnectionId);
    }

    @mobx.action
    public removeChildConnectionById(meetingConnectionId: number, childJoinMethodId: number): void {
        const connection = this._connections[meetingConnectionId];
        if (!connection && !connection.childConnections) return;

        const childIndex = connection.childConnections.findIndex(child => child.joinMethodId === childJoinMethodId);
        if (childIndex !== -1) {
            connection.childConnections.splice(childIndex, 1);
        }
        this.deleteChildConnectionInParent(childJoinMethodId, meetingConnectionId);
    }

    @mobx.action
    public removeConnectionGroupById(meetingConnectionId: number): void {
        delete this.connectionGroups[meetingConnectionId];
        this.invalidateAssignedConnectionById(meetingConnectionId);
    }

    @mobx.action
    public removeMeetingConnectionFromGroups(connection: IConnectionDetailsDTO): void {
        Object.values(this.connectionGroups).forEach(group => {
            const idx = group.joinMethods.findIndex(jm => jm.joinMethodId === connection.joinMethodId);
            if (idx !== -1) {
                group.joinMethods.splice(idx, 1);
            }
        });
    }

    @mobx.action
    public invalidateAssignedConnectionById(id: number): void {
        super.invalidateAssignedConnectionById(id);
        this.invalidateMeetingRoleConnectionById(id);

        organizationRoleList.forEach(
            role => this.getOrganizationsByRole(role)
                .forEach(org => org.invalidateAssignedConnectionById(id))
        );
    }

    public getOrganizationsList(): IAttendeeOrganizationDTO[] {
        let connectionOrganizations = [];
        organizationRoleList.forEach(role => {
            connectionOrganizations = mergeConnectionOrganizations(
                connectionOrganizations,
                this.getOrganizationListByRole(role),
                true
            );
        });
        return connectionOrganizations;
    }

    public getMeetingConnectionById(meetingConnectionId: number): IConnectionDetailsDTO | IConnectionGroupDTO {
        return this._connections[meetingConnectionId] || this.connectionGroups[meetingConnectionId];
    }

    public getFilteredMeetingConnectionList(type: ConnectionType): IConnectionDetailsDTO[] {
        return Object.values(this._connections).filter((connection: IConnectionDetailsDTO) => connection.type !== type);
    }

    public getParentConnectionList(): IParentConnectionDTO[] {
        return Object.values(this.parentConnections);
    }

    @mobx.action
    public setParentConnectionList(parentConnectionId: number,connection: IChildConnectionDTO): void{
        let parent = this.parentConnections[parentConnectionId];
        if (!parent) {
            parent = { meetingConnectionId: parentConnectionId, children: [connection] };
            this.parentConnections[parentConnectionId] = parent;
        } else {
            const existingChildIndex = parent.children.findIndex(
                child => child.attendeeId === connection.attendeeId
            );
            if (existingChildIndex !== -1) {
                parent.children[existingChildIndex] = connection;
            } else {
                parent.children.push(connection);
            }
        }
    }

    @mobx.action.bound
    public updateChildConnectionInParent(parentConnectionId: number, childConnectionDetails: Partial<IChildConnectionDTO>): void {
        const parent = this.parentConnections[parentConnectionId];
        if (!parent) return;

        for (const child of parent.children) {
            if (child.joinMethodId === childConnectionDetails.joinMethodId) {
                this.updateChildConnectionDetails(child, childConnectionDetails, parentConnectionId);
                return;
            }
        }
    }

    @mobx.action.bound
    public deleteChildConnectionInParent(childJoinMethodId: number, parentConnectionId: number): void {
        const parent = this.parentConnections[parentConnectionId];
        if (!parent) return;

        const idx = parent.children.findIndex(child => child.joinMethodId === childJoinMethodId);
        if (idx !== -1) {
            parent.children.splice(idx, 1);
        }
    }

    public getMeetingConnectionList(): IConnectionDetailsDTO[] {
        return Object.values(this._connections);
    }

    @mobx.action
    public setMeetingConnectionGroup(group: IConnectionGroupDTO): void {
        this.connectionGroups[group.meetingConnectionId] = group;
    }

    public getMeetingConnectionGroupList(): IConnectionGroupDTO[] {
        return Object.values(this.connectionGroups);
    }

    public getKnovioReplayConnection(): IConnectionDetailsDTO | undefined {
        const { endDateTime } = this.getProps();
        const now = new Date();
        const endTime = new Date(endDateTime);
        return isBefore(endTime, now)
            ? Object.values(this._connections).find(c => (c.type === ConnectionType.Knovio || c.type === ConnectionType.Other) && c.passportReplayEnabled)
            : undefined
        ;
    }

    public isKnovioReplayConnectionExist(connection: IConnectionDetailsDTO): boolean {
        if (connection.type !== ConnectionType.Knovio && connection.type !== ConnectionType.Other) return false;

        const connections = Object.values(this._connections);

        // new connection
        if (!connection.meetingConnectionId) {
            return !connections.find(c => (c.type === ConnectionType.Knovio || c.type === ConnectionType.Other) && c.passportReplayEnabled);
        }

        // edit connection
        const currentKnovioWithReplay = connections.find(c => (c.type === ConnectionType.Knovio || c.type === ConnectionType.Other) && c.passportReplayEnabled);
        if (!currentKnovioWithReplay || currentKnovioWithReplay.meetingConnectionId === connection.meetingConnectionId) {
            return true;
        }
        return false;
    }

    @mobx.action
    public setOperators(operators: IOperatorsDTO[]): void {
        this.operators = {};
        MeetingConnectionsAggregateRoot.mapItemsByKey(operators, 'email', this.operators);
    }
    @mobx.action
    public removeOperatorByEmail(email: string): void {
        delete this.operators[email];
    }

    @mobx.action
    public onOperatorRoleChange = (email: string, role: OperatorRole): void => {
        this.operators[email] = { ...this.operators[email], operatorRole: role };
    };

    public getOperatorList(): IOperatorsDTO[] {
        return Object.values(this.operators);
    }

    @mobx.action
    public setOrganizationsToRole(role: OrganizationRole, organizations: IConnectionOrganizationDTO[]): void {
        existedOrganizationRoleGuard(role);
        this[role] = {};
        MeetingConnectionsAggregateRoot.mapItemsByKey(
            organizations,
            'organizationId',
            this[role],
            (props: IConnectionOrganizationDTO) => OrganizationMeetingConnection.create(props, role)
        );
    }

    public getOrganizationsByRole(role: OrganizationRole) : OrganizationMeetingConnection[] {
        return Object.values(this[role]);
    }

    public getOrganizationListByRole(role: OrganizationRole): IConnectionOrganizationDTO[] {
        return Object
            .values(this[role])
            .map(organization => organization.toDTO())
        ;
    }

    public getOrganizationsListWithOrgRole(): IAttendeeOrganizationDTO[] {
        let connectionOrganizations = [];
        organizationRoleList.forEach(role => {
            connectionOrganizations = mergeConnectionOrganizations(
                connectionOrganizations,
                this.getOrganizationListByRole(role)
                .map(item => ({
                    ...item,
                    attendees: item.attendees.map(a => ({
                        attendeOrganizationRole: role,
                        ...a
                    }))
                })),
                true
            );
        });
        return connectionOrganizations;
    }

    private getOrganizationIdsByRole = (role: OrganizationRole): number[] => Object.keys(this[role]).map(Number);

    private getOrganizationsIds = (): number[] => [
        ...new Set(organizationRoleList
            .reduce((_ids, role) => _ids.concat(this.getOrganizationIdsByRole(role)), []))
    ];

    @mobx.action
    public removeOrganizationByRole(role: OrganizationRole, organizationId: number): void {
        const filteredParticipants = this.getOrganizationListByRole(role)
            .filter(org => org.organizationId !== organizationId);

        this.setOrganizationsToRole(role, filteredParticipants);
    }

    private getAttendeesByOrganizationId = (organizationId: number): {attendeeId: number}[] =>
        organizationRoleList
            .reduce((attendees, role) => attendees.concat(this[role]?.[organizationId]?.toDTO()?.attendees ?? []), []);

    private getOrganizationAttendeesById = (organizationId: number): {organizationId: number; attendees: {attendeeId: number}[]} => {
        return {
            organizationId: organizationId,
            attendees: this.getAttendeesByOrganizationId(organizationId)
        };
    };

    public getOrganizationAttendees() {
        return this.getOrganizationsIds()
            .map(this.getOrganizationAttendeesById);
    }

    /* [QU-10768] Returns all Role-assigned attendees grouped by Organization IDs. */
    public getMeetingOrganizationAttendees() {
        return (this.getProps()?.registrants ?? [])
            .reduce((organizations, org) => {
               const idx = organizations.findIndex(({ organizationId }) => org.organizationId === organizationId);
               if (idx === -1) {
                   organizations[organizations.length] = { organizationId: org.organizationId, attendees: org.attendees };
               } else {
                   organizations[idx].attendees = organizations[idx].attendees.concat(org.attendees);
               }
               return organizations;
            }, this.getOrganizationAttendees());
    }

    public getRoomAssignedConnection(): null | IConnectionDetailsDTO {
        return this.getProps()?.room?.defaultConnection ?? null;
    }

    public getRoomRoleAssignedConnection(role: OrganizationRole): null | IConnectionDetailsDTO {
        if (role === OrganizationRole.Presenters) return this.getProps()?.room?.presentersConnection ?? null;
        if (role === OrganizationRole.Hosts) return this.getProps()?.room?.hostsConnection ?? null;
        return null;
    }

    public hasMeetingRoleAssignedConnection(role: OrganizationRole): boolean {
        return Reflect.has(this.meetingRoleConnectionIds, role);
    }

    public getMeetingRoleConnectionId(role: OrganizationRole): number | null {
        return this.hasMeetingRoleAssignedConnection(role)
            ? this.meetingRoleConnectionIds[role]
            : null;
    }

    public getMeetingRoleAssignedConnection(role: OrganizationRole): null | IConnectionDetailsDTO | IConnectionGroupDTO {
        const connectionId = this.getMeetingRoleConnectionId(role);
        if (!connectionId) return null;
        return this.getMeetingConnectionById(connectionId) ?? null;
    }

    public getRoleAssignedConnection(role: OrganizationRole): null | IConnectionDetailsDTO | IConnectionGroupDTO {
        return this.getMeetingRoleAssignedConnection(role) ?? this.getRoomRoleAssignedConnection(role);
    }

    public getDerivedConnectionForRole(role: OrganizationRole): null | IConnectionDetailsDTO | IConnectionGroupDTO {
        const meetingDefaultConnection = this.hasAssignedConnection()
            ? this.getMeetingConnectionById(this.getAssignedConnection())
            : null;

        return [
            this.getMeetingRoleAssignedConnection(role),
            this.getRoomRoleAssignedConnection(role),
            meetingDefaultConnection,
            this.getRoomAssignedConnection()
        ].find(Boolean) || null;
    }

    public getDerivedConnectionForMeetingRoleDropdown(role: OrganizationRole): null | IConnectionDetailsDTO | IConnectionGroupDTO {
        const meetingDefaultConnection = this.hasAssignedConnection()
            ? this.getMeetingConnectionById(this.getAssignedConnection())
            : null;

        return [
            this.getRoomRoleAssignedConnection(role),
            meetingDefaultConnection,
            this.getRoomAssignedConnection()
        ].find(Boolean) || null;
    }

    @mobx.action
    public assignMeetingRoleConnection(role: OrganizationRole, connectionId: number): void {
        existedOrganizationRoleGuard(role);
        if (!meetingRoleConnectionFieldByRole[role]) return;
        connectionIdValueTypeGuard(connectionId);
        this.meetingRoleConnectionIds[role] = connectionId;
    }

    @mobx.action
    public removeMeetingRoleConnection(role: OrganizationRole): void {
        if (this.hasMeetingRoleAssignedConnection(role)) {
            delete this.meetingRoleConnectionIds[role];
        }
    }

    private initializeMeetingRoleConnection(role: OrganizationRole, connectionId?: number | null): void {
        if (connectionId) this.assignMeetingRoleConnection(role, connectionId);
    }

    private meetingRoleConnectionsToDTO(dtoRef: Partial<IMeetingConnectionsDTO>): void {
        Object.entries(meetingRoleConnectionFieldByRole).forEach(([role, field]) => {
            if (this.hasMeetingRoleAssignedConnection(role as OrganizationRole)) {
                dtoRef[field] = this.getMeetingRoleConnectionId(role as OrganizationRole);
            } else {
                delete dtoRef[field];
            }
        });
    }

    @mobx.action
    private invalidateMeetingRoleConnectionById(id: number): void {
        Object.keys(this.meetingRoleConnectionIds).forEach((role: OrganizationRole) => {
            if (this.meetingRoleConnectionIds[role] === id) {
                this.removeMeetingRoleConnection(role);
            }
        });
    }

    public getMeetingConnectionsListByType(type: ConnectionType): IConnectionDetailsDTO[] {
        return Object.values(this._connections).filter((connection: IConnectionDetailsDTO) => connection.type === type);
    }

    public getConnectionsListFromGroup = (assignedGroup: IConnectionGroupDTO, attendeeId: number): IDropdownConnectionGroupDetails[] => {
        const connectionsListFromGroup: IDropdownConnectionGroupDetails[] = [];
        assignedGroup.joinMethods
            .filter(j => !!j.order)
            .sort((a, b) => a.order - b.order)
            .forEach(({ joinMethodId }) => {
                const connection = this.getFilteredMeetingConnectionList(ConnectionType.UniqueDialIn).
                    find(c => c.joinMethodId === joinMethodId)
                ;
                const parentConnection = this.getParentConnectionList().find(c => c.meetingConnectionId === connection.meetingConnectionId);
                const parentGrop = this.getParentConnectionList().find(c => c.meetingConnectionId === assignedGroup.meetingConnectionId);

                const childConnection = parentConnection
                    ? parentGrop?.children.find(c => c.attendeeId === attendeeId && c.parentJoinMethodId === joinMethodId)
                    : undefined
                ;
                connectionsListFromGroup.push({
                    connection: connection,
                    attendeeParentConnection: parentConnection,
                    attendeeChildConnection: childConnection
                });
            })
        ;
        return connectionsListFromGroup;
    };

    private updateChildConnectionDetails(childConnection: IChildConnectionDTO, details: Partial<IChildConnectionDTO>, parentConnectionId: number): void {
        switch (childConnection.type) {
            case ConnectionType.UniqueJoiningLink:
                this.updateChildUniqueJoiningLinkConnectionDetails(childConnection, details);
                return;
            case ConnectionType.UniqueDialIn:
                this.updateChildUniqueDialInConnectionDetails(childConnection, details);
                return;
            case ConnectionType.PanelistLink:
                this.updateChildPanelistLinkConnectionDetails(details, parentConnectionId);
                return;
            default:
                return;
        }
    }

    @mobx.action.bound
    private updateChildPanelistLinkConnectionDetails(details: Partial<IChildConnectionDTO>, parentConnectionId: number): void {
        this.setMeetingConnection(details, true, parentConnectionId, details as IChildConnectionDTO);
        this.setParentConnectionList(parentConnectionId, details as IChildConnectionDTO);
    }

    @mobx.action.bound
    private updateChildUniqueDialInConnectionDetails(childConnection: IChildConnectionDTO, details: Partial<IChildConnectionDTO>): void {
        childConnection.uniqueDialInCode = details.uniqueDialInCode;
        childConnection.userId = details.userId;
    }

    @mobx.action.bound
    private updateChildUniqueJoiningLinkConnectionDetails(childConnection: IChildConnectionDTO, details: Partial<IChildConnectionDTO>): void {
        childConnection.connectionLink = details.connectionLink;
    }

    private transpileFormFieldValueIfFromCKEditor = (name: string, value: string): string => {
        return name === formFields.MEETING_CONNECTION_NOTES
            ? transpileClassesToInlineStyles(value)
            : value;
    };
};
